import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/formatting";
import EmptySetIcon from "src/assets/icons/empty-set.svg?react";
import { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";

type Props = {
    scoreValue: NumericalScoreValue | undefined;
    refScoreValue: number | undefined;
    precision?: number;
};

export const computeScoreComparisonPercent = (
    scoreValue: NumericalScoreValue | undefined,
    refScoreValue: number | undefined,
): number | undefined => {
    if (scoreValue === null || scoreValue === undefined) return undefined;
    if (refScoreValue === null || refScoreValue === undefined || refScoreValue === 0) return undefined;
    return ((scoreValue - refScoreValue) / Math.abs(refScoreValue)) * 100;
};

export const GridScoreComparisonDisplay = (props: Props) => {
    const { i18n } = useTranslation();
    const theme = useTheme();

    const { scoreValue, refScoreValue, precision = 0 } = props;

    const percent = computeScoreComparisonPercent(scoreValue, refScoreValue);

    if (percent === undefined) {
        return <EmptySetIcon width="20px" title={"No comparison"} />;
    }

    const sign = percent > 0 ? "+" : "";
    const formatted = formatNumber(percent, i18n.resolvedLanguage, {
        maximumFractionDigits: precision,
        minimumFractionDigits: precision,
    }).replace(/ /g, "\u00a0");

    const displayStr = `${sign}${formatted}\u00a0%`;

    // For environmental impact: negative = improvement (green), positive = degradation (red)
    const color =
        percent < 0 ? theme.palette.success.main
        : percent > 0 ? theme.palette.error.main
        : theme.palette.text.secondary;

    return (
        <Typography
            component="p"
            title={displayStr}
            sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "initial",
                lineHeight: "normal",
                minWidth: 0,
            }}
        >
            <Typography variant="number-small" component="span" sx={{ color }}>
                {displayStr}
            </Typography>
        </Typography>
    );
};
